import { Application, Container, Graphics } from 'pixi.js';

// ── Tile definitions ──────────────────────────────────────────────────────────
export type TileType = 'grass' | 'dirt' | 'water' | 'stone';

export interface TileData {
  type: TileType;
}

export interface MapData {
  cols: number;
  rows: number;
  tileSize: number;
  tiles: TileData[][];
}

export interface ZoneData {
  cx: number;
  cy: number;
  radius: number;
}

const TILE_COLORS: Record<TileType, number> = {
  grass: 0x5a9e3d,
  dirt:  0xb07d3c,
  water: 0x3a7abf,
  stone: 0x888888,
};

const TILE_STROKE: Record<TileType, number> = {
  grass: 0x4a8a2d,
  dirt:  0x9a6d2c,
  water: 0x2a6aaf,
  stone: 0x666666,
};

function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/**
 * World manages the layered Pixi scene and camera.
 *
 * Layer order inside worldContainer:
 *   mapContainer       — static tiled map
 *   itemContainer      — pickups / loot
 *   playerContainer    — player entities (local + remote)
 *   projectileContainer— bullets, thrown items
 *   zoneOverlay        — zone red overlay + circle border
 *
 * HUD goes directly on app.stage (camera-independent).
 */
export class World {
  // ── Containers ────────────────────────────────────────────────────────────
  readonly worldContainer:      Container;
  readonly mapContainer:        Container;
  readonly itemContainer:       Container;
  readonly playerContainer:     Container;
  readonly projectileContainer: Container;
  private  zoneOverlay:         Container;

  // ── Camera state ──────────────────────────────────────────────────────────
  private camX = 0;
  private camY = 0;

  constructor(private app: Application) {
    this.worldContainer      = new Container();
    this.mapContainer        = new Container();
    this.itemContainer       = new Container();
    this.playerContainer     = new Container();
    this.projectileContainer = new Container();
    this.zoneOverlay         = new Container();

    this.worldContainer.addChild(
      this.mapContainer,
      this.itemContainer,
      this.playerContainer,
      this.projectileContainer,
      this.zoneOverlay,
    );

    this.app.stage.addChild(this.worldContainer);
  }

  // ── Camera ─────────────────────────────────────────────────────────────────

  /**
   * Smoothly pan the camera towards (targetX, targetY) in world-space.
   * Call once per frame from the game loop.
   * @param alpha  0..1 — lower = smoother/slower (0.1 is typical)
   */
  panCamera(targetX: number, targetY: number, alpha = 0.1): void {
    this.camX = lerp(this.camX, targetX, alpha);
    this.camY = lerp(this.camY, targetY, alpha);

    this.worldContainer.x = this.app.screen.width  / 2 - this.camX;
    this.worldContainer.y = this.app.screen.height / 2 - this.camY;
  }

  /** Snap the camera instantly (no lerp). */
  snapCamera(x: number, y: number): void {
    this.camX = x;
    this.camY = y;
    this.worldContainer.x = this.app.screen.width  / 2 - x;
    this.worldContainer.y = this.app.screen.height / 2 - y;
  }

  // ── Map ────────────────────────────────────────────────────────────────────

  /**
   * Draw a tiled map into mapContainer, replacing any previous draw.
   */
  drawMap(map: MapData): void {
    this.mapContainer.removeChildren();

    const g = new Graphics();
    const { cols, rows, tileSize, tiles } = map;

    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const tile = tiles[row]?.[col];
        if (!tile) continue;

        const x = col * tileSize;
        const y = row * tileSize;
        const color  = TILE_COLORS[tile.type];
        const stroke = TILE_STROKE[tile.type];

        g.rect(x, y, tileSize, tileSize);
        g.fill({ color });
        g.stroke({ color: stroke, width: 1, alpha: 0.4 });
      }
    }

    this.mapContainer.addChild(g);
  }

  // ── Zone ───────────────────────────────────────────────────────────────────

  /**
   * Draw (or redraw) the zone overlay:
   *  - Semi-transparent red fill outside the safe circle.
   *  - Bright red circle border on the safe zone perimeter.
   */
  drawZone(zone: ZoneData): void {
    this.zoneOverlay.removeChildren();

    const { cx, cy, radius } = zone;

    // ── Red fill mask: large rect with a circular hole cut out ────────────────
    // We use an even-odd fill so the circle acts as a hole.
    const overlay = new Graphics();

    // Outer rectangle large enough to cover the whole map with margin
    const MARGIN = 4000;
    overlay.rect(-MARGIN, -MARGIN, MARGIN * 2, MARGIN * 2);

    // Draw the safe-zone circle in the same path — Pixi v8 Graphics uses
    // even-odd winding when you draw overlapping shapes, so the circle
    // subtracts from the rectangle, leaving only the outer area filled.
    overlay.circle(cx, cy, radius);

    overlay.fill({ color: 0xff0000, alpha: 0.28 });

    // ── Circle border (solid red ring) ────────────────────────────────────────
    const ring = new Graphics();
    ring.circle(cx, cy, radius);
    ring.stroke({ color: 0xff2222, width: 3, alpha: 0.9 });

    this.zoneOverlay.addChild(overlay, ring);
  }

  /** Remove the zone overlay (safe zone covers the whole map). */
  clearZone(): void {
    this.zoneOverlay.removeChildren();
  }

  // ── Cleanup ────────────────────────────────────────────────────────────────

  destroy(): void {
    this.app.stage.removeChild(this.worldContainer);
    this.worldContainer.destroy({ children: true });
  }
}
